/**
 * Every threshold the image tracer decides by, in one place.
 *
 * These are rules of thumb: first set by hand on generated drawings (`test-drawings`), to be
 * tuned against real scans with the evaluation harness (`scripts/evaluate-trace.ts`, see
 * `trace-samples/README.md`). The defaults are what the tracer does; `traceSketch` takes any of
 * them overridden, which is how the harness tries a change before anyone makes it.
 *
 * Sizes given as a "share" are of the drawing — its shorter side, or the building's area — so
 * they mean the same at any resolution. Sizes "in strokes" are in widths of a line as drawn,
 * thickened a pixel each side. Metres are real sizes, used once the plan's scale is known.
 *
 * What is not here is geometry rather than judgement: how many points a door's quarter circle
 * is drawn with, where along a shape it is sampled. And the fittings' templates live with the
 * fittings (`SYMBOL_TEMPLATES` in `symbol-shapes`), because they are measured off drawings.
 */

export interface Range {
  readonly min: number;
  readonly max: number;
}

export interface TraceConfig {
  readonly ink: {
    /** How much darker than its surroundings a pixel has to be to be ink. */
    readonly localContrast: number;
  };
  readonly skew: {
    /** A turn smaller than this, in degrees, is how the drawing was drawn, not how it was photographed. */
    readonly minimumDegrees: number;
    /** The most a picture is turned to square it up; further round, it is drawn at that angle. */
    readonly rangeDegrees: number;
  };
  readonly sheetFrame: {
    /** A frame round the sheet covers this share of the picture each way… */
    readonly cover: number;
    /** …lies within this share of its own size of its edge… */
    readonly band: number;
    /** …with this share of its ink there… */
    readonly edgeShare: number;
    /** …and has a drawing at least this share of its size inside it. */
    readonly inner: number;
  };
  readonly structure: {
    /** Ink joined to the walls spans at least this share of the drawing's width or height. */
    readonly span: number;
    /** Ink standing free but this large a share of the drawing, outside any room, is a wing drawn apart. */
    readonly largeIslandShare: number;
  };
  readonly openings: {
    /** Openings up to this share of the drawing's shorter side are closed, until the doors give a real size. */
    readonly defaultShare: number;
    /** A wall end this share of the shorter side long (or 4 strokes), pointing at an opening, is a wall, not a letter's stroke. */
    readonly wallRunShare: number;
    /** A gap with wall on one side only is closed up to this share of a doorway: a corridor's mouth stays open. */
    readonly oneSidedShare: number;
    /** A doorway drawn as a plain gap: from this many doors' widths across to this many. */
    readonly doors: Range;
    /** How much of its turned box a doorway closed across fills; less is a wedge where walls meet aslant. */
    readonly fill: number;
  };
  readonly secondRead: {
    /** The doorway read again with: this many times the doors' width… */
    readonly doorFactor: number;
    /** …or, with no doors, this many desks' lengths… */
    readonly deskFactor: number;
    /** …when that differs from the first read's by more than this share of it. */
    readonly change: number;
  };
  readonly rooms: {
    /** A room is at least this share of the building's area… */
    readonly minShare: number;
    /** …and less than this: more is the open floor itself. */
    readonly openFloorShare: number;
    /** A patch filling this much of its box is a rectangle, and its box is its outline. */
    readonly rectangularFill: number;
  };
  readonly desks: {
    /** How much of its turned box a desk fills. */
    readonly fill: number;
    /** How long a desk is against how deep. */
    readonly aspect: Range;
    /** How big a desk is, as a share of the building's area. */
    readonly share: Range;
    /** How big a desk really is, when the plan's scale is known. */
    readonly metres: { readonly length: Range; readonly depth: Range };
  };
  readonly doors: {
    /** A door swing: no longer than this many times as wide… */
    readonly aspect: number;
    /** …filling this much of its box, a quarter circle… */
    readonly fill: Range;
    /** …and this share of the drawing's shorter side across. */
    readonly size: Range;
  };
  readonly scale: {
    /** How wide a door is, in metres: what a plan's scale is read from first. */
    readonly doorWidthMetres: number;
    /** How long a desk is, in metres: what it is read from when there are no doors. */
    readonly deskLengthMetres: number;
  };
  readonly stairs: {
    /** A step is at least this many times as long as it is deep… */
    readonly treadAspect: number;
    /** …and a staircase at least this many side by side. */
    readonly minTreads: number;
  };
  readonly windows: {
    /** A window is a strip at least this many times as long as it is wide, set in an outside wall. */
    readonly aspect: number;
  };
  readonly marks: {
    /** Letters, chairs and other small marks are no larger than this share of the drawing's shorter side. */
    readonly markShare: number;
    /** Letters are no taller than this share of it. */
    readonly letterShare: number;
  };
  readonly columns: {
    /** A column is a solid, squarish block no larger than this share of the shorter side. */
    readonly share: number;
  };
  readonly straighten: {
    /** An edge of a wall or room within this many degrees of level or plumb is drawn level or plumb. */
    readonly degrees: number;
    /** Only an edge of furniture this near level or plumb is made so: furniture is kept as drawn. */
    readonly furnitureDegrees: number;
  };
  readonly walls: {
    /** A straight run of wall is at least this many strokes long… */
    readonly minBarStrokes: number;
    /** …and this share of the drawing's shorter side. */
    readonly minBarShare: number;
    /** A run continues the wall beside it when they overlap by this share of the shorter… */
    readonly barOverlap: number;
    /** …and neither is more than this many times the other's length. */
    readonly barLengthRatio: number;
    /** A wall is drawn thick at this many strokes across… */
    readonly thickFactor: number;
    /** …and at least this many pixels more than a stroke. */
    readonly thickExtraPixels: number;
  };
  readonly angledWalls: {
    /** A direction is read along when this share of the drawing's edges runs that way… */
    readonly minShare: number;
    /** …it is this many degrees or more from level and plumb… */
    readonly axisMargin: number;
    /** …and it is among this many strongest. */
    readonly most: number;
    /** A direction this much of the drawing follows also says how wide its lines are. */
    readonly strokeShare: number;
    /** Two walls found in directions this many degrees apart, one over the other, are one wall. */
    readonly sameWallDegrees: number;
    /** A wall found turned is this many times as long as thick, or it is where walls meet. */
    readonly aspect: number;
  };
  readonly objects: {
    /**
     * Shapes on the floor drawn with gaps up to twice this many strokes — a corner a pen did not
     * quite close — are read as closed too. 0 reads only shapes drawn closed.
     */
    readonly gapClosingStrokes: number;
  };
  readonly asDrawn: {
    /** Ink this many strokes from a find that replaces it is the find's own: its outline, a wall's faces. */
    readonly coverStrokes: number;
    /** Ink up to this share of the drawing's shorter side outside the building is kept too. */
    readonly marginShare: number;
    /** A piece of ink smaller across than this many strokes is a speck, not something drawn. */
    readonly speckStrokes: number;
    /**
     * An outline is kept within this many pixels of the ink's edge. Under half a pixel, so a
     * slant or a curve keeps its shape and loses only the pixels' steps along it.
     */
    readonly tolerancePixels: number;
    /** At most this many points between all the shapes kept: enough for any plan, not so many the editor slows. */
    readonly maxPoints: number;
  };
  readonly symbols: {
    /**
     * A fitting is kept as one only when this sure; below it, it stays furniture. Above the
     * review's line for "to check" (0.6), because a fitting that is not there, on the plan, is
     * worse than one missed, which the administrator marks with a click.
     */
    readonly minConfidence: number;
    /** A fitting is no bigger than this share of the drawing's shorter side. */
    readonly maxShare: number;
  };
}

export const DEFAULT_TRACE_CONFIG: TraceConfig = {
  ink: { localContrast: 0.15 },
  skew: { minimumDegrees: 0.4, rangeDegrees: 20 },
  sheetFrame: {
    cover: 0.85, band: 0.03, edgeShare: 0.85, inner: 0.3,
  },
  structure: { span: 0.25, largeIslandShare: 0.03 },
  openings: {
    defaultShare: 0.075, wallRunShare: 0.04, oneSidedShare: 0.85, doors: { min: 0.65, max: 2.2 }, fill: 0.7,
  },
  secondRead: { doorFactor: 1.3, deskFactor: 0.75, change: 0.3 },
  rooms: { minShare: 0.003, openFloorShare: 0.6, rectangularFill: 0.85 },
  desks: {
    fill: 0.8,
    aspect: { min: 1.25, max: 4 },
    share: { min: 0.0003, max: 0.012 },
    metres: { length: { min: 0.9, max: 2.6 }, depth: { min: 0.45, max: 1.3 } },
  },
  doors: { aspect: 1.35, fill: { min: 0.64, max: 0.87 }, size: { min: 0.012, max: 0.12 } },
  scale: { doorWidthMetres: 0.85, deskLengthMetres: 1.6 },
  stairs: { treadAspect: 2.8, minTreads: 4 },
  windows: { aspect: 5 },
  marks: { markShare: 0.025, letterShare: 0.06 },
  columns: { share: 0.05 },
  straighten: { degrees: 12, furnitureDegrees: 4 },
  walls: {
    minBarStrokes: 4, minBarShare: 0.03, barOverlap: 0.6, barLengthRatio: 2.5, thickFactor: 1.4, thickExtraPixels: 2,
  },
  angledWalls: {
    minShare: 0.02, axisMargin: 6, most: 4, strokeShare: 0.15, sameWallDegrees: 10, aspect: 3,
  },
  objects: { gapClosingStrokes: 2 },
  asDrawn: {
    coverStrokes: 1.5, marginShare: 0.02, speckStrokes: 1, tolerancePixels: 0.4, maxPoints: 100000,
  },
  symbols: { minConfidence: 0.66, maxShare: 0.25 },
};

/** Any part of the config, to be laid over the defaults. */
export type TraceConfigOverride = {
  readonly [Group in keyof TraceConfig]?: {
    readonly [Key in keyof TraceConfig[Group]]?: TraceConfig[Group][Key] extends Range
      ? Partial<Range>
      : TraceConfig[Group][Key] extends { readonly length: Range; readonly depth: Range }
        ? { readonly length?: Partial<Range>; readonly depth?: Partial<Range> }
        : TraceConfig[Group][Key];
  };
};

const isObject = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value);

const laidOver = (base: unknown, over: unknown): unknown => {
  if (!isObject(base) || !isObject(over)) return over === undefined ? base : over;
  const merged: Record<string, unknown> = { ...base };
  Object.entries(over).forEach(([key, value]) => { merged[key] = laidOver(base[key], value); });
  return merged;
};

/** The defaults with `override` laid over them, however deep. */
export const traceConfigOf = (override: TraceConfigOverride = {}): TraceConfig => laidOver(DEFAULT_TRACE_CONFIG, override) as TraceConfig;
